import React, { Suspense, useCallback, useEffect } from 'react';

import { isNil } from 'lodash-es';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Route, Routes, useMatch } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { isFalse } from 'utils';

import AppHeader from 'components/app-header';
import AppflowUpdateProgress from 'components/appflow-update-progress';
import { AutoSelectRestaurant } from 'components/auto-select-restaurant';
import { BottomServiceMode } from 'components/bottom-service-mode';
import ErrorNetwork from 'components/error-network';
import Footer from 'components/footer';
import LanguageSelectorModal from 'components/language-selector-modal';
import { regionFilter } from 'components/language-selector/regionFilter';
import LoadingAnimation from 'components/loading-animation';
import { FIXED_TOAST_HEIGHT } from 'components/menu-order-wizard/fixed-add-to-order-and-quantity/constants';
import MobileNavigation from 'components/mobile-web-navigation-bar';
import SetLoading from 'components/set-loading';
import SiteUpdating from 'components/site-updating';
import { ToastContainer } from 'components/toast';
import WelcomeMessage from 'components/welcome-message';
import {
  useFeatureAccountFormQuery,
  useFeatureAccountQuery,
  useFeatureMenuQuery,
} from 'generated/sanity-graphql';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import useLoyaltyRedirect from 'hooks/loyalty/use-loyalty-redirect';
import { useResetStoreResetCartResetServiceModeTimeout } from 'hooks/reset-store-timeout';
import { useAppHeaderDimensions } from 'hooks/use-app-header-dimensions';
import { useAppflow } from 'hooks/use-appflow';
import useDatadogRUM from 'hooks/use-datadog-rum';
import useEffectOnce from 'hooks/use-effect-once';
import { useFeatureHomePageQuery } from 'hooks/use-feature-home-page-query';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import { useForter } from 'hooks/use-forter';
import useGoogleAnalytics from 'hooks/use-google-analytics';
import { useIosAtt } from 'hooks/use-ios-att';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { usePrevious } from 'hooks/use-previous';
import { useSetUserLocale } from 'hooks/use-set-user-locale';
import { useTrendingData } from 'hooks/use-trending-data';
import LanguageSelectorPage from 'pages/language-selector';
import { InRestaurantRedemptionDialogStatus } from 'pages/loyalty/loyalty-in-restaurant-redemption-modal-status';
import LoyaltyOptInModal from 'pages/loyalty/loyalty-opt-in-modal';
import { useFeaturesContext } from 'state/features';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { NEXT_URL_QUERY_PARAM } from 'state/location/constants';
import { useLogger } from 'state/logger';
import { useLoyaltyContext } from 'state/loyalty';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { useNetworkContext } from 'state/network';
import { useOrderContext } from 'state/order';
import { usePageManagerContext } from 'state/static-page-manager';
import { useStaticPageRoutes } from 'state/static-page-manager/hooks/use-static-page-routes';
import { useUIContext } from 'state/ui';
import { theme } from 'styles/configure-theme';
import { UUID_REGEX_STRING } from 'utils/constants';
import { fullBrandName } from 'utils/environment';
import { isItemPickerOrComboRoute, routes } from 'utils/routing';

import brandRoutes from './brand-routes';
import { IS_TH } from './constants';
import { DefaultCookieBannerContainer, OneTrustCookieBanner } from './cookie-banner-container';
import { scrollTo } from './scroll-to';
import { skeletonColor } from './skeleton-theme';
import useLogPageView from './use-log-page-view';
import useRobotMetaTags from './use-robot-meta-tags';
import useScrollTop from './use-scroll-top';
import { LazyRoute, lazyWithFallback } from './util';

const Cart: LazyRoute = lazyWithFallback(() => import('pages/cart'));
const MainMenu: LazyRoute = lazyWithFallback(() => import('pages/main-menu'));
const MenuContent: LazyRoute = lazyWithFallback(() => import('pages/menu-content'));
const Home: LazyRoute = lazyWithFallback(() => import('pages/home'));
const Account: LazyRoute = lazyWithFallback(() => import('pages/account'));
const CommPreferences: LazyRoute = lazyWithFallback(() => import('pages/account/comm-preferences'));
const AccountInfo: LazyRoute = lazyWithFallback(() => import('pages/account/account-info'));
const AccountDelete: LazyRoute = lazyWithFallback(() => import('pages/account/account-delete'));
const AccountRequestInfo: LazyRoute = lazyWithFallback(() =>
  import('pages/account/account-request-info')
);
const PaymentMethods: LazyRoute = lazyWithFallback(() => import('pages/account/payment-methods'));
const Diagnostics: LazyRoute = lazyWithFallback(() => import('pages/diagnostics'));
const AccountOrders: LazyRoute = lazyWithFallback(() => import('pages/account/account-orders'));
const Offers: LazyRoute = lazyWithFallback(() => import('pages/offers'));
const LoyaltyHome: LazyRoute = lazyWithFallback(() => import('pages/loyalty/loyalty-home'));
const LoyaltyDashboard: LazyRoute = lazyWithFallback(() =>
  import('pages/loyalty/loyalty-dashboard')
);
const LoyaltySurprise: LazyRoute = lazyWithFallback(() => import('pages/loyalty/loyalty-surprise'));
const LoyaltyInRestaurantRedemption: LazyRoute = lazyWithFallback(() =>
  import('pages/loyalty/loyalty-in-restaurant-redemption')
);
const LoyaltyClaimPoints: LazyRoute = lazyWithFallback(() =>
  import('pages/loyalty/loyalty-claim-points')
);
const SignUp: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-up'));
const SignIn: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-in'));
const Quiz: LazyRoute = lazyWithFallback(() => import('components/quiz'));
const OnboardingModal: LazyRoute = lazyWithFallback(() =>
  import('pages/account/tims-rewards/onboarding-modal')
);

const AuthChallengeScreenJwt: LazyRoute = lazyWithFallback(() =>
  import('pages/authentication/auth-email-jwt')
);
const ConfirmJwt: LazyRoute = lazyWithFallback(() => import('pages/authentication/confirm-jwt'));
const ConfirmOtp: LazyRoute = lazyWithFallback(() => import('pages/authentication/confirm-otp'));
const SignOut: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-out'));
const StoreLocator: LazyRoute = lazyWithFallback(() => import('pages/store-locator'));
const OrderConfirmation: LazyRoute = lazyWithFallback(() =>
  import('pages/order-confirmation/order-confirmation')
);
const StoreOrderConfirmation: LazyRoute = lazyWithFallback(() =>
  import('pages/order-confirmation/store/store-order-confirmation')
);
const About: LazyRoute = lazyWithFallback(() => import('pages/about'));
const StaticPage: LazyRoute = lazyWithFallback(() => import('pages/static'));
const NotFound: LazyRoute = lazyWithFallback(() => import('pages/not-found'));
const SupportPage: LazyRoute = lazyWithFallback(() => import('pages/support'));
const SupportV2CategoriesPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-categories-page')
);
const SupportV2CategoryPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-category-page')
);
const SupportV2FormPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-form-page')
);
const SupportV2OrdersListPage: LazyRoute = lazyWithFallback(() =>
  import('pages/support-v2/support-v2-orders-list-page')
);
const ReportOrderIssuePage: LazyRoute = lazyWithFallback(() => import('pages/report-order-issue'));
const ReportOrderNeverArrivedPage: LazyRoute = lazyWithFallback(() =>
  import('pages/report-order-never-arrived')
);
const ReportOrderItemsMissingPage: LazyRoute = lazyWithFallback(() =>
  import('pages/report-order-items-missing')
);
const OfferActivatePage: LazyRoute = lazyWithFallback(() => import('pages/offer-activate'));
const SendGiftPage: LazyRoute = lazyWithFallback(() => import('pages/send-gift'));
const AcceptGift = lazyWithFallback(() => import('pages/accept-gift'));
const History = lazyWithFallback(() => import('pages/account/history'));

const LayoutContainer = styled.div<{ $bottomInset: number }>`
  background: ${Styles.color.background};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-x: hidden;
  width: 100%;
  padding-bottom: calc(env(safe-area-inset-bottom) + ${p => p.$bottomInset}px);
  &:focus {
    top: 0.2rem;
    left: 0.2rem;
    opacity: 1;
  }
  min-height: calc(100vh - ${p => p.$bottomInset}px);
  ${Styles.desktop`
    min-height: 100vh;
    #router-wrapper {
      min-height: 100vh; /* Fixes div collapse bug DON'T REMOVE */
    }
  `}

  ${Styles.mobileFullscreen`
    position: relative;
    margin-top: env(safe-area-inset-top); //Not 100% sure this works on android
  `}
`;

type ContentContainerProps = {
  $topInset: number;
  $bottomInset: number;
};

const ContentContainer = styled.div<ContentContainerProps>`
  background: ${Styles.color.background};
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  ${Styles.mobile`
    // add padding equivalent to the height of the header minus
    // the safe area view (already accounted for in LayoutContainer's margin-top)
    padding-top: ${(p: ContentContainerProps) =>
      `calc(${p.$topInset}px - env(safe-area-inset-top))`};
    min-height: ${(p: ContentContainerProps) =>
      `calc(100vh - ${p.$topInset}px - env(safe-area-inset-top) - ${p.$bottomInset}px)`};
  `}
`;

const ContentLoadingContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  background: ${Styles.color.background};
`;

interface IRouterWrapperProps {
  $topInset: number;
  $bottomInset: number;
}
const RouterWrapper = styled.div<IRouterWrapperProps>`
  background: ${Styles.color.background};
  flex-grow: 2;
  min-height: ${({ $topInset, $bottomInset }) =>
    `calc(100vh - ${$topInset}px - env(safe-area-inset-top) - ${$bottomInset}px)`};
`;

const ContentRouting = () => {
  useGoogleAnalytics();
  const { clearServerOrder } = useOrderContext(); // @todo type order context
  const { appHeaderDimensions } = useAppHeaderDimensions();
  const { appFooterDimensions, bottomServiceDimensions } = useUIContext();

  const { formatMessage } = useIntl();
  const { claimPointsEnabled, loyaltyEnabled } = useLoyaltyContext();
  const { inRestaurantRedemptionEnabled } = useInRestaurantRedemptionContext();

  const enableCatering = useFlag(LaunchDarklyFlag.ENABLE_CATERING);
  const enableOrderSupport = useFlag(LaunchDarklyFlag.ENABLE_ORDER_SUPPORT);
  const enableAccountDeletePage = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_DELETE_PAGE);

  const enableRequestMyInformation = useFlag(LaunchDarklyFlag.ENABLE_REQUEST_MY_INFORMATION);
  const enableCommPrefFlag = useFlag(LaunchDarklyFlag.ENABLE_COMMUNICATION_PREFERENCES);
  const enableCommunicationPreferences = !isFalse(enableCommPrefFlag);

  const enableInAppQuizGame = useFlag(LaunchDarklyFlag.ENABLE_IN_APP_QUIZ_GAME);

  const enableOfferGifting = useFlag(LaunchDarklyFlag.ENABLE_OFFER_GIFTING);

  const enableGuestAndMultiStepSupportForm = useFlag(
    LaunchDarklyFlag.ENABLE_GUEST_AND_MULTI_STEP_SUPPORT_PAGE
  );

  // Show the offer page if it's not TH or if it's enabled for TH via LD
  const enableThOffersPage = useFlag(LaunchDarklyFlag.ENABLE_TH_OFFERS_PAGE);
  const showOffersPage = !IS_TH || enableThOffersPage;

  const enableSignUpFlag = useFlag(LaunchDarklyFlag.ENABLE_SIGN_UP);
  const enableSignUp = isNil(enableSignUpFlag) || enableSignUpFlag;

  const forceAuthOnHomepageFlag = useFlag(LaunchDarklyFlag.FORCE_HOMEPAGE_AUTH);
  const isBaseRoute = useMatch(routes.base);

  const forceAuthOnHomepage = forceAuthOnHomepageFlag && enableSignUp && isBaseRoute;

  useAuthRedirects({ skip: !forceAuthOnHomepage });

  useEffect(() => {
    // reset the server order when a user navigate into the menu no matter what
    clearServerOrder();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Base Routes */}
      <ContentContainer
        $topInset={appHeaderDimensions.height}
        $bottomInset={appFooterDimensions.height + bottomServiceDimensions.height}
      >
        <RouterWrapper
          $topInset={appHeaderDimensions.height}
          $bottomInset={appFooterDimensions.height + bottomServiceDimensions.height}
        >
          <Routes>
            <Route path={'*'} element={<StaticPage />} />
            <Route path={routes.notFound404} element={<NotFound />} />
            <Route path={routes.confirmJwt} element={<ConfirmJwt />} />
            <Route path={routes.confirmOtp} element={<ConfirmOtp />} />
            {loyaltyEnabled && (
              <Route
                path={`${formatMessage({ id: 'routes.loyaltyDashboard' })}`}
                element={<LoyaltyDashboard />}
              />
            )}
            <Route
              path={`${formatMessage({ id: 'routes.loyaltyHome' })}/${formatMessage({
                id: 'surprise',
              })}`}
              element={<LoyaltySurprise />}
            />
            {inRestaurantRedemptionEnabled && (
              <Route
                path={formatMessage({ id: 'routes.redemptionInRestaurant' })}
                element={<LoyaltyInRestaurantRedemption />}
              />
            )}
            {claimPointsEnabled && (
              <Route
                path={`${formatMessage({ id: 'routes.claimPointsBase' })}/*`}
                element={<LoyaltyClaimPoints />}
              />
            )}
            {enableSignUp && (
              <>
                <Route path={routes.signUp} element={<SignUp />} />
                <Route path={formatMessage({ id: 'routes.signUp' })} element={<SignUp />} />
                <Route path={routes.signIn} element={<SignIn />} />
                <Route path={formatMessage({ id: 'routes.signIn' })} element={<SignIn />} />
                <Route path={routes.account} element={<Account />} />
                <Route path={formatMessage({ id: 'routes.account' })} element={<Account />} />
              </>
            )}
            <Route path={routes.authChallengeJwt} element={<AuthChallengeScreenJwt />} />
            <Route path={routes.signOut} element={<SignOut />} />
            <Route path={routes.menu} element={<MainMenu />} />
            <Route path={routes.menuRecentItems} element={<MainMenu />} />
            <Route path={`${routes.menu}/:id`} element={<MenuContent />} />
            <Route path={routes.base} element={<Home />} />
            {showOffersPage && <Route path={`${routes.offers}/*`} element={<Offers />} />}

            <Route path={formatMessage({ id: 'routes.loyaltyHome' })} element={<LoyaltyHome />} />
            <Route
              path={`${formatMessage({ id: 'routes.loyaltyHome' })}//*`}
              element={<LoyaltyHome />}
            />
            <Route path={routes.info} element={<AccountInfo />} />
            <Route
              path={`${formatMessage({ id: 'routes.rewardsHistory' })}`}
              element={<History />}
            />
            {enableCommunicationPreferences && (
              <Route path={routes.communication} element={<CommPreferences />} />
            )}
            <Route path={`${routes.orders}/*`} element={<AccountOrders />} />
            {enableAccountDeletePage && (
              <Route
                path={formatMessage({ id: 'routes.accountDelete' })}
                element={<AccountDelete />}
              />
            )}
            {enableRequestMyInformation && (
              <Route
                path={formatMessage({ id: 'routes.accountRequestInfo' })}
                element={<AccountRequestInfo />}
              />
            )}
            {enableInAppQuizGame && <Route path={routes.quiz} element={<Quiz />} />}
            {enableInAppQuizGame && (
              <Route path={formatMessage({ id: 'routes.quiz' })} element={<Quiz />} />
            )}
            <Route path={`${routes.payment}/*`} element={<PaymentMethods />} />
            <Route
              path={`${formatMessage({ id: 'routes.rewardsOnboarding' })}/:stepIndex`}
              element={<OnboardingModal />}
            />
            <Route
              path={`${formatMessage({ id: 'routes.rewardsOnboarding' })}`}
              element={<OnboardingModal />}
            />
            <Route path={routes.about} element={<About />} />
            <Route path={`${routes.diagnostics}/*`} element={<Diagnostics />} />
            {enableOrderSupport && (
              <Route
                path={`${routes.reportOrderIssue}/:orderId`}
                element={<ReportOrderIssuePage />}
              />
            )}
            {enableOrderSupport && (
              <Route
                path={`${routes.missingOrder}/:orderId`}
                element={<ReportOrderNeverArrivedPage />}
              />
            )}
            {enableOrderSupport && (
              <Route
                path={`${routes.missingItems}/:orderId`}
                element={<ReportOrderItemsMissingPage />}
              />
            )}
            {enableOfferGifting && (
              <>
                <Route path={formatMessage({ id: 'routes.sendGift' })} element={<SendGiftPage />} />
                <Route path={`${routes.acceptGift}/:token`} element={<AcceptGift />} />
              </>
            )}
            <Route
              path={`${routes.offerActivate}/:tokenId/:offerId`}
              element={<OfferActivatePage />}
            />
            {enableGuestAndMultiStepSupportForm ? (
              // general support requests
              <>
                {/* // general support requests */}
                <Route path={routes.supportForm} element={<SupportV2FormPage />} />
                <Route path={routes.supportOrders} element={<SupportV2OrdersListPage />} />
                <Route path={routes.supportCategories} element={<SupportV2CategoriesPage />} />
                <Route path={routes.supportCategory} element={<SupportV2CategoryPage />} />
                <Route path={routes.support} element={<SupportV2CategoriesPage />} />
              </>
            ) : (
              <Route path={routes.support} element={<SupportPage />} />
            )}

            {/* We only want brand-specific routes to be available for those brands */}
            {/* `brandRoutes` can be thought of a Map of routes (string) to Components (React component) */}
            {Object.entries(brandRoutes).map(([path, Component]) =>
              // if a route isn't catering we want it. if it is catering, we leave it up to the LD flag
              path !== routes.catering || enableCatering ? (
                <Route
                  path={formatMessage({ id: path })}
                  element={<Component key={path} />}
                  key={path}
                />
              ) : null
            )}
          </Routes>
        </RouterWrapper>
        <Footer />
      </ContentContainer>
    </>
  );
};

const Layout = () => {
  const {
    location: { pathname },
    navigate,
  } = useLocationContext();
  const {
    featureAccountId = '',
    featureIdsLoading,
    featureHomePageId = '',
    featureAccountFormId = '',
    featureMenuId = '',
  } = useFeaturesContext();
  const { hasNetworkError } = useNetworkContext();
  const { routesLoading } = usePageManagerContext();
  const { appFooterDimensions, bottomServiceDimensions } = useUIContext();

  const { loading: isFeatureAccountLoading } = useFeatureAccountQuery({
    variables: { featureAccountId },
    skip: !featureAccountId || pathname !== routes.communication || pathname !== routes.orders,
  });
  const { featureNavigationLoading: isFeatureNavigationLoading } = useFeatureNavigation();
  const { loading: isTrendingContentLoading } = useTrendingData();
  const { loading: isMainMenuContentLoading } = useFeatureMenuQuery({
    variables: { featureMenuId },
    skip: !featureMenuId || pathname !== routes.menu,
  });
  const { loading: isSignUpContentLoading } = useFeatureAccountFormQuery({
    variables: { featureAccountFormId },
    skip: !featureAccountFormId || pathname !== routes.signUp,
  });

  /* TODO: figure out how to get the loading status
   *       without double-triggering the Sanity call
   * CONTEXT:
   *  this code still fires off the Sanity query even
   *    though it is already fired from useFeatureHomePage hook
   *  setting skip: true means that loading always comes back false
   *  not passing anything means we still hit Sanity, but it returns
   *    an error that id is required
   *  💭 maybe moving feature home page data to context so that we
   *    have the data and loading state available multiple places?
   */
  const { loading: isFeatureHomePageLoading } = useFeatureHomePageQuery({
    variables: { featureHomePageId },
    skip: !featureHomePageId,
  });
  const { loading: isStaticRoutesLoading } = useStaticPageRoutes();
  const { inRestaurantRedemptionEnabled } = useInRestaurantRedemptionContext();

  useDatadogRUM();
  useForter();
  useLogPageView();
  useScrollTop();
  useSetUserLocale();
  const {
    setShowLanguageSelectorModal,
    setHasShownLocaleSelector,
    showLanguageSelectorModal,
    language,
  } = useLocale();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const isDesktop = useMediaQuery(MediaQuery.Desktop);
  const { formatMessage } = useIntl();
  const logger = useLogger();

  // NOTE: This should be a noop for all brands EXCEPT TH. We only need this code
  // for a short time in order to redirect US users stuck on the TH CA native experience.
  // SEE: https://rbictg.atlassian.net/browse/SHOP-2103
  useLoyaltyRedirect();

  const showSiteUpdating = useFlag(LaunchDarklyFlag.SHOW_SITE_UPDATING);
  // Prompts user for location services at start of session and selects nearest store
  const enableAutoSelectRestaurant = useFlag(LaunchDarklyFlag.ENABLE_AUTO_SELECT_RESTAURANT);
  const enableCookieBanner = useFlag(LaunchDarklyFlag.ENABLE_COOKIE_BANNER);
  const enableOneTrustCookieBanner = useFlag(
    LaunchDarklyFlag.ENABLE_ONE_TRUST_COOKIE_CONSENT_BANNER
  );
  const enableFixedAddToOrderAndQuantityButton = useFlag(
    LaunchDarklyFlag.ENABLE_FIXED_ADD_TO_ORDER_AND_QUANTITY_BUTTON
  );
  const displayFixedAddToOrderAndQuantity =
    enableFixedAddToOrderAndQuantityButton && isItemPickerOrComboRoute(pathname) && !isDesktop;
  const geoLocationAvailable = navigator.geolocation;

  const forceBlockingUpdate = new URL(window.location.href).searchParams.get('forceBlockingUpdate');

  const {
    error: appflowError,
    extractingUpdate,
    needsBlockingUpdate,
    permissionToUpdate,
    progress,
  } = useAppflow({
    logger,
    forceBlockingUpdate: !!forceBlockingUpdate,
  });

  const { requestIosAttPermissions } = useIosAtt();

  const handleModalDismiss = () => {
    setHasShownLocaleSelector();
    setShowLanguageSelectorModal(false);
  };

  let content;

  const prevAmount = usePrevious(showSiteUpdating);

  // setup bot noindex nofollow meta tags to required routes
  // See hook and CBA-243 for more details
  const robotMetaTags = useRobotMetaTags(pathname);

  useEffectOnce(() => {
    const nextLocationQueryStringParam = new URL(window.location.href).searchParams.get(
      NEXT_URL_QUERY_PARAM
    );

    if (nextLocationQueryStringParam) {
      const decodedNexRoute = decodeURIComponent(nextLocationQueryStringParam);

      // Check for relative path but do not accept schemaless urls to avoid open redirect vulnerability
      if (/^\/[^/]/.test(decodedNexRoute)) {
        navigate(decodedNexRoute, { replace: true });
      }
    }
  });

  useResetStoreResetCartResetServiceModeTimeout();

  useEffect(() => {
    if (prevAmount !== showSiteUpdating) {
      // reload the page if we are changing from true to false.
      // That means a new site was loaded
      if (prevAmount === true && showSiteUpdating === false) {
        window?.location?.reload();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevAmount]);

  useEffect(requestIosAttPermissions, []);

  const scrollToTop = useCallback(() => scrollTo({ top: 0 }), []);

  if (showSiteUpdating) {
    return (
      <ThemeProvider theme={theme}>
        <SiteUpdating />
      </ThemeProvider>
    );
  }

  if (needsBlockingUpdate && !hasNetworkError) {
    return (
      <ThemeProvider theme={theme}>
        <AppflowUpdateProgress
          extractingUpdate={extractingUpdate}
          hasError={!!appflowError}
          progress={progress}
        />
      </ThemeProvider>
    );
  }

  // Temporary solution, the goal is to make object for each pathname
  const getLoadingAriaLabel = (path: string) => {
    if (path === routes.menu) {
      return formatMessage({ id: 'loadingRestaurantMenu' });
    }
    return formatMessage({ id: 'loading' });
  };

  const blockingLoadingStates = (path: string) => {
    // The ultimate goal here is to preload what the critical pages will use,
    // without holding up the user waiting for content that isn't needed on the page.
    // As we extend this pattern, more routes should be added to this switch statement.
    // This should become the only "full page spinner" in the app, so we know it when we see it.
    switch (path) {
      case routes.base:
        return [featureIdsLoading, isFeatureHomePageLoading];
      case routes.offers:
        return [featureIdsLoading];
      case routes.about:
        return [featureIdsLoading, isFeatureNavigationLoading];
      case routes.orders:
      case routes.communication:
        return [featureIdsLoading, isFeatureAccountLoading];
      case routes.menu:
        return [featureIdsLoading, isMainMenuContentLoading];
      case routes.signUp:
        return [featureIdsLoading, isSignUpContentLoading];
      case routes.trending:
        return [featureIdsLoading, isTrendingContentLoading];
      case routes.storeOrderConfirmation:
        return [featureIdsLoading, routesLoading];
      default:
        return [routesLoading, isStaticRoutesLoading];
    }
  };

  if (blockingLoadingStates(pathname).some(queryIsLoading => !!queryIsLoading)) {
    content = (
      <ContentLoadingContainer aria-label={getLoadingAriaLabel(pathname)}>
        <LoadingAnimation fillColor={Styles.color.primary} />
      </ContentLoadingContainer>
    );
  } else if (hasNetworkError) {
    content = <ErrorNetwork />;
  } else {
    content = (
      <Suspense fallback={<SetLoading />}>
        <Routes>
          {/* Full page routes */}
          <Route
            path={`${routes.cart}/*`}
            element={<Cart permissionToUpdate={permissionToUpdate} />}
          />
          <Route path={routes.storeOrderConfirmation} element={<StoreOrderConfirmation />} />
          <Route path={`${routes.orderConfirmation}/:rbiOrderId`} element={<OrderConfirmation />} />
          <Route path={`${routes.storeLocator}/*`} element={<StoreLocator />} />
          <Route path={`${routes.languageSelector}`} element={<LanguageSelectorPage />} />
          <Route
            path={`${formatMessage({ id: 'routes.storeLocator' })}/*`}
            element={<StoreLocator />}
          />
          {/* Regular routes (with normal nav, header, footer, etc) */}
          <Route path="*" element={<ContentRouting />} />
        </Routes>
      </Suspense>
    );
  }

  const getToastBottomInset = () => {
    if (displayFixedAddToOrderAndQuantity) {
      return FIXED_TOAST_HEIGHT;
    }

    // Some routes on mobile did not have a bottom navigation nor bottom service bar so we need set the toast's bottomInset to be 0px
    const mobileRoutesWithoutBottomNavbar = [
      routes.cart,
      formatMessage({ id: 'routes.rollUpTheRim' }),
      new RegExp(`${routes.orderConfirmation}/${UUID_REGEX_STRING}`),
    ];

    if (mobileRoutesWithoutBottomNavbar.some(route => !!pathname.match(route))) {
      return '0px';
    }

    return `${isMobile ? appFooterDimensions.height : 0}px`;
  };

  // Loading animation shown when waiting to determine updated needed
  // prevents content flashing on mobile apps
  if (needsBlockingUpdate === null) {
    return (
      <ThemeProvider theme={theme}>
        <Helmet titleTemplate={`${fullBrandName()} - %s`} defaultTitle={fullBrandName()}>
          <html lang={language} />
        </Helmet>
        <ContentLoadingContainer aria-label={getLoadingAriaLabel(pathname)}>
          <LoadingAnimation fillColor={Styles.color.primary} />
        </ContentLoadingContainer>
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <Helmet titleTemplate={`${fullBrandName()} - %s`} defaultTitle={fullBrandName()}>
        <html lang={language} />
      </Helmet>
      <ToastContainer bottomInset={getToastBottomInset()} />
      {robotMetaTags}

      <SkeletonTheme color={skeletonColor}>
        <AppHeader />
        <LayoutContainer
          role="main"
          id="main"
          $bottomInset={appFooterDimensions.height + bottomServiceDimensions.height}
        >
          {content}
        </LayoutContainer>
        <LoyaltyOptInModal />
        {inRestaurantRedemptionEnabled && <InRestaurantRedemptionDialogStatus />}
        {(appFooterDimensions.height || !isMobile) && <BottomServiceMode />}
        <MobileNavigation scrollToTop={scrollToTop} />
        {showLanguageSelectorModal && (
          <LanguageSelectorModal regionFilter={regionFilter} onModalDismiss={handleModalDismiss} />
        )}
        {enableAutoSelectRestaurant && geoLocationAvailable && <AutoSelectRestaurant />}
        {enableCookieBanner ? (
          <DefaultCookieBannerContainer />
        ) : (
          enableOneTrustCookieBanner && <OneTrustCookieBanner />
        )}
        <WelcomeMessage />
      </SkeletonTheme>
    </ThemeProvider>
  );
};

export default Layout;
